// Serialize a Pen path (closed) into an SVG path "d" string for the texture
// pipeline. The PenCanvas pattern mode normalizes the path into a 64×64 viewBox
// before calling this — so coordinates here are expected to be inside [0..64].
//
// We emit Q (quadratic), C (cubic), L (line), and arc-as-cubic-approximation
// since SVG arcs use a different parameterization than our 3-point arcMid.

import { tessellatePath, type PenPath } from '../core/penTool';

const VIEW = 64;

/** Map (z, r) → SVG (x, y). For pattern mode, we keep z = x and r = y unchanged
 *  inside the 64×64 box. Caller must already have normalized into [0..64]. */
function pt(p: { z: number; r: number }): string {
  return `${p.z.toFixed(2)},${p.r.toFixed(2)}`;
}

/** Convert a Pen path (closed) into an SVG `<path d=...>` string. We use the
 *  flat tessellation as a polyline (Lines only) — robust across all anchor
 *  kinds and arc parameterization differences. SDF pipeline doesn't care about
 *  curve smoothness, only the inside/outside boundary. */
export function penPathToSvgD(path: PenPath): string {
  if (path.anchors.length < 3) return '';
  const poly = tessellatePath(path, 96);
  if (poly.length === 0) return '';
  let d = `M ${pt(poly[0])}`;
  for (let i = 1; i < poly.length; i++) d += ` L ${pt(poly[i])}`;
  if (path.closed) d += ' Z';
  return d;
}

/** Compose multiple closed paths into a single SVG document.
 *  Caller controls fill/background. White-on-black = base SDF convention
 *  (filled = "inside the drawing", which becomes the accent material). */
export function penPathsToSvg(paths: PenPath[]): string {
  const ds = paths.map((p) => penPathToSvgD(p)).filter(Boolean);
  if (ds.length === 0) return '';
  const body = ds.map((d) => `<path d="${d}" fill="#fff" stroke="none" fill-rule="evenodd"/>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VIEW} ${VIEW}" width="${VIEW}" height="${VIEW}"><rect width="${VIEW}" height="${VIEW}" fill="#000"/>${body}</svg>`;
}

/** Convenience: normalize a path's bounding box into [0..64] before serializing.
 *  When `centered` is true, preserves aspect ratio and centers within the box. */
export function normalizeAndSerialize(path: PenPath): string {
  if (path.anchors.length < 3) return '';
  let zMin = Infinity, zMax = -Infinity, rMin = Infinity, rMax = -Infinity;
  for (const a of path.anchors) {
    if (a.z < zMin) zMin = a.z; if (a.z > zMax) zMax = a.z;
    if (a.r < rMin) rMin = a.r; if (a.r > rMax) rMax = a.r;
  }
  const span = Math.max(1e-3, Math.max(zMax - zMin, rMax - rMin));
  const scale = (VIEW - 8) / span; // 4px margin each side
  const offX = (VIEW - (zMax - zMin) * scale) / 2 - zMin * scale;
  const offY = (VIEW - (rMax - rMin) * scale) / 2 - rMin * scale;
  const norm: PenPath = {
    closed: true,
    anchors: path.anchors.map((a) => ({
      ...a,
      z: a.z * scale + offX,
      r: a.r * scale + offY,
      cp1: a.cp1 ? { z: a.cp1.z * scale + offX, r: a.cp1.r * scale + offY } : undefined,
      cp2: a.cp2 ? { z: a.cp2.z * scale + offX, r: a.cp2.r * scale + offY } : undefined,
      arcMid: a.arcMid ? { z: a.arcMid.z * scale + offX, r: a.arcMid.r * scale + offY } : undefined
    }))
  };
  return penPathsToSvg([norm]);
}
